import React, { useCallback, useEffect, useState } from "react";
import { Loader2, ScrollText, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const actionTone = (a) => {
    if (a?.startsWith("user.delete") || a?.endsWith(".decline")) return "border-destructive/30 text-destructive bg-destructive/5";
    if (a?.startsWith("user.suspend")) return "border-amber-500/30 text-amber-600 bg-amber-500/10";
    if (a?.endsWith(".approve") || a?.includes("reactivate")) return "border-emerald-500/30 text-emerald-600 bg-emerald-500/10";
    return "border-border text-muted-foreground bg-muted/40";
};

export default function AuditLog() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actor, setActor] = useState("");
    const [action, setAction] = useState("");
    const [targetType, setTargetType] = useState("any");
    const [limit, setLimit] = useState("100");

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const params = { limit };
            if (actor) params.actor = actor;
            if (action) params.action = action;
            if (targetType !== "any") params.target_type = targetType;
            const { data } = await api.get("/admin/audit", { params });
            setRows(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [actor, action, targetType, limit]);

    useEffect(() => { load(); }, [load]);

    return (
        <div data-testid="admin-audit-log">
            <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
                <div>
                    <p className="overline">Command Center</p>
                    <h1 data-testid="dash-page-title" className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                        Audit Log
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">Every important admin action, chronological.</p>
                </div>
                <button onClick={load} className="btn-outline text-xs" data-testid="audit-refresh">Refresh</button>
            </div>

            {/* Quick filter chips — one-click presets for the most common
                audit slices. Passport deletions are highlighted for consent
                / GDPR review. */}
            <div className="mb-3 flex flex-wrap items-center gap-2">
                <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">Quick filters:</span>
                <button
                    onClick={() => { setAction("passport.delete"); setTargetType("any"); setActor(""); }}
                    className={`text-[11px] px-2.5 py-1 rounded-full border transition ${action === "passport.delete" ? "border-rose-500/60 bg-rose-500/10 text-rose-700 dark:text-rose-400" : "border-border hover:bg-muted/40 text-muted-foreground"}`}
                    data-testid="audit-quick-passport-deletions"
                >
                    Passport deletions
                </button>
                <button
                    onClick={() => { setAction("admin."); setTargetType("any"); setActor(""); }}
                    className={`text-[11px] px-2.5 py-1 rounded-full border transition ${action === "admin." ? "border-sky-500/60 bg-sky-500/10 text-sky-700 dark:text-sky-400" : "border-border hover:bg-muted/40 text-muted-foreground"}`}
                    data-testid="audit-quick-admin-actions"
                >
                    Admin team actions
                </button>
                {(action || actor || targetType !== "any") && (
                    <button
                        onClick={() => { setAction(""); setActor(""); setTargetType("any"); }}
                        className="text-[11px] px-2.5 py-1 rounded-full text-muted-foreground hover:text-foreground underline"
                        data-testid="audit-quick-clear"
                    >
                        Clear filters
                    </button>
                )}
            </div>

            <div className="card-soft p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="relative">
                    <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input value={actor} onChange={(e) => setActor(e.target.value)} placeholder="Actor…" className="pl-8 h-9" data-testid="audit-actor" />
                </div>
                <Input value={action} onChange={(e) => setAction(e.target.value)} placeholder="Action (e.g. brief.approve)" className="h-9" data-testid="audit-action" />
                <Select value={targetType} onValueChange={setTargetType}>
                    <SelectTrigger className="h-9" data-testid="audit-target-type"><SelectValue placeholder="Any target type" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="any">Any target type</SelectItem>
                        <SelectItem value="user">User</SelectItem>
                        <SelectItem value="hiring_brief">Hiring brief</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={limit} onValueChange={setLimit}>
                    <SelectTrigger className="h-9" data-testid="audit-limit"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="50">Latest 50</SelectItem>
                        <SelectItem value="100">Latest 100</SelectItem>
                        <SelectItem value="250">Latest 250</SelectItem>
                        <SelectItem value="500">Latest 500</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {loading ? (
                <div className="py-16 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>
            ) : rows.length === 0 ? (
                <div className="card-soft p-10 text-center">
                    <ScrollText size={22} className="mx-auto text-muted-foreground" />
                    <div className="mt-2 font-heading font-semibold">No audit entries.</div>
                    <div className="mt-1 text-xs text-muted-foreground">Actions will appear here once admins moderate briefs or users.</div>
                </div>
            ) : (
                <div className="card-soft overflow-hidden">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-border text-left text-[11px] uppercase text-muted-foreground">
                                <th className="p-3 font-semibold">When</th>
                                <th className="p-3 font-semibold">Actor</th>
                                <th className="p-3 font-semibold">Action</th>
                                <th className="p-3 font-semibold">Target</th>
                                <th className="p-3 font-semibold">Details</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id} className="border-b border-border/60 last:border-0 hover:bg-muted/30" data-testid={`audit-row-${r.id}`}>
                                    <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">
                                        {r.at ? new Date(r.at).toLocaleString() : "—"}
                                    </td>
                                    <td className="p-3 text-xs text-foreground truncate max-w-[220px]">{r.actor}</td>
                                    <td className="p-3">
                                        <Badge className={`text-[10px] border ${actionTone(r.action)}`}>{r.action}</Badge>
                                    </td>
                                    <td className="p-3 text-xs">
                                        {r.target_type && <span className="text-muted-foreground">{r.target_type}: </span>}
                                        <span className="text-foreground font-mono text-[11px]">{r.target?.slice(0, 12) || "—"}</span>
                                    </td>
                                    <td className="p-3 text-xs text-muted-foreground max-w-[400px]">
                                        {r.meta && Object.keys(r.meta).length > 0 ? (
                                            <span className="line-clamp-2">{Object.entries(r.meta).map(([k, v]) => `${k}: ${String(v).slice(0, 60)}`).join(" · ")}</span>
                                        ) : "—"}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
